import "react-datepicker/dist/react-datepicker.css";
import { useEffect, useMemo, useState } from "react";
import { ReportApi } from "src/services/api/ReportApi";
import {
  ColDef,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  LoadSuccessParams,
  GridApi,
  SizeColumnsToContentStrategy,
} from "ag-grid-enterprise";
import { AgBarSeriesOptions, AgChartOptions } from "ag-charts-enterprise";
import { CardDefundingTransactionDetailModel } from "src/models/CardDefundingTransactionDetailModel";
import useSidebar from "src/hooks/useSidebar";
import useReference from "src/hooks/useReference";
import useTheme from "src/hooks/useTheme";
import CardDefundingTransactionDetailSummaryData from "./CardDefundingTransactionDetailSummaryData";
import { AgCharts } from "ag-charts-react";
import { CardDefundingTransactionDetailSummaryModel } from "src/models/CardDefundingTransactionDetailSummaryModel";
import BaseGrid from "../../BaseGrid";
import ValidationService from "src/services/ValidationService";
import { CardAmountByDate } from "src/models/CardAmountByDate";
import useReport from "src/hooks/useReport";
import { downloadFile } from "src/utils/DownloadFile";
import SearchCriteria from "src/components/SearchCriteria";
import LoadingComponent from "src/components/LoadingComponent";

const CardDefundingTransactionDetail = () => {
  const { setIsOpen } = useSidebar();
  const { appSettings } = useReference();
  const { theme } = useTheme();
  const {
    reportInputs,
    setExceedsMaxDateRange,
    mapReportRequest,
    currencyFormatter,
    defaultColDef,
    getContextMenuItems,
    cardNumberFormatter,
  } = useReport();
  const [summaryDetail, setSummaryDetail] = useState<CardDefundingTransactionDetailSummaryModel>(
    {} as CardDefundingTransactionDetailSummaryModel
  );
  const [chartData, setChartData] = useState<CardAmountByDate[]>([]);
  const [gridReady, setGridReady] = useState(false);
  const [dataSource, setDataSource] = useState<IServerSideDatasource>();
  const [gridApi, setGridApi] = useState<GridApi>();
  const [isDownload, setIsDownload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const nextOptions = { ...options };
    nextOptions.theme = theme === "dark" ? "ag-material-dark" : "ag-material";
    setOptions(nextOptions);
  }, [theme]);

  const handleViewReport = () => {
    if (
      ValidationService.isValidDateRange(
        reportInputs?.fromDate,
        reportInputs?.toDate,
        appSettings.dateRangeMonthsLimit_View
      )
    ) {
      setIsDownload(false);
      setExceedsMaxDateRange(false);
      setGridReady(true);
      setSummaryDetail({} as CardDefundingTransactionDetailSummaryModel);
      setChartData([]);
      setDataSource(createServerSideDatasource());
    } else {
      setExceedsMaxDateRange(true);
    }
  };

  const handleDownloadReport = () => {
    if (
      ValidationService.isValidDateRange(
        reportInputs?.fromDate,
        reportInputs?.toDate,
        appSettings.dateRangeMonthsLimit_Download
      )
    ) {
      setIsDownload(true);
      setIsLoading(true);
      setExceedsMaxDateRange(false);
      ReportApi.exportCardDefundingTransactionDetails(mapReportRequest(null))
        .then((res) => {
          downloadFile(CardDefundingTransactionDetail.displayName, res.data);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setExceedsMaxDateRange(true);
    }
  };

  const [colDefs] = useState<ColDef<CardDefundingTransactionDetailModel>[]>([
    { field: "transactionId" },
    { field: "clientReferenceNumber", cellDataType: "text" },
    { field: "carrierNumber", cellDataType: "text" } as any,
    { field: "merchantGroup" },
    { field: "description" },
    { field: "cardNumber", valueFormatter: cardNumberFormatter },
    { field: "externalId" },
    { field: "cardType" },
    { field: "campaign", cellDataType: "text" },
    {
      headerName: "Expiration Period",
      field: "expirationPeriodDisplay",
      sortable: false,
      cellDataType: "text",
    },
    {
      headerName: "Card Amount",
      field: "fundAmount",
      aggFunc: "sum",
      valueGetter: (params) => params.data?.amount,
      valueFormatter: currencyFormatter,
      cellStyle: { textAlign: "right", paddingRight: "50px" },
    },
    {
      headerName: "Item Total",
      field: "amount",
      aggFunc: "sum",
      valueGetter: (params) => params.data?.amount,
      valueFormatter: currencyFormatter,
      cellStyle: { textAlign: "right", paddingRight: "50px" },
    },
    { headerName: "Type", field: "description" },
    { headerName: "CSR", field: "csr" },
    { field: "date" },
    { field: "time" }
  ]);

  const autoSizeStrategy = useMemo<SizeColumnsToContentStrategy>(() => {
    return {
      type: "fitCellContents",
    };
  }, []);

  const createServerSideDatasource: () => IServerSideDatasource = () => {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        getReportData(params);
      },
    };
  };

  const getReportData = (params: IServerSideGetRowsParams | null) => {
    setIsOpen(false);
    ReportApi.getCardDefundingTransactionDetails(mapReportRequest(params))
      .then((res) => {
        const result = res.data;
        result?.rows?.map((x: CardDefundingTransactionDetailModel) => {
          if (x.date) x.date = new Date(x.date).toLocaleDateString();
          if ((x as any).clientOrderDate)
            (x as any).clientOrderDate = new Date((x as any).clientOrderDate).toLocaleDateString();
          return x;
        });
        const successParams: LoadSuccessParams = {
          rowData: result.rows,
          rowCount: result.totalCount,
        };
        if ((result?.rows?.length ?? 0) > 0) {
          params?.api?.hideOverlay();
          params?.success(successParams);
        } else {
          params?.api?.showNoRowsOverlay();
          params?.success({ rowData: [], rowCount: 0 });
        }
        setSummaryDetail(result.summaryDetail);
        const cd = mapChartData(result.summaryDetail);
        setChartData(cd);
        setOptions((prevOptions) => ({
          ...prevOptions,
          data: cd,
        }));
      })
      .catch(() => {
        params?.fail();
      });
  };

  const mapChartData = (data: CardDefundingTransactionDetailSummaryModel) => {
    const cardAmountsByDate: CardAmountByDate[] = [];
    data.cardAmounts?.forEach((ca) => {
      cardAmountsByDate.push({
        amount: ca.amount,
        date: new Date(ca.date).toLocaleDateString(),
      });
    });
    
    if ((cardAmountsByDate.length ?? 0) === 0 && data.transactionDetails) {
      data.transactionDetails.forEach((td) =>
        cardAmountsByDate.push({ amount: td.amount, date: new Date(td.date).toLocaleDateString() })
      );
    }
    return cardAmountsByDate;
  };

  const [options, setOptions] = useState<AgChartOptions>({
    title: {
      text: "Card Amount Total By Date",
    },
    data: chartData as CardAmountByDate[],
    series: [
      {
        type: "bar",
        xKey: "date",
        yKey: "amount",
      } as AgBarSeriesOptions,
    ],
    theme: theme === "dark" ? "ag-material-dark" : "ag-material",
  });

  return (
    <>
      <div className="report-container">
        <div className="report-header">
          <h5 className="mb-0">Card Defunding Transaction Detail</h5>
          {(reportInputs?.programId ?? 0) > 0 && reportInputs?.fromDate && reportInputs?.toDate && (
            <div className="report-header-date">{`${reportInputs?.fromDate.toLocaleDateString()} - ${reportInputs?.toDate.toLocaleDateString()}`}</div>
          )}
        </div>

        <div className="blocks">
          <SearchCriteria
            usesLocations={false}
            usesUsers={false}
            usesCampaigns={false}
            handleViewReport={handleViewReport}
            handleDownloadReport={handleDownloadReport}
          />

          {(chartData?.length ?? 0) > 0 && !isDownload && (
            <div className="report-summary">
              <CardDefundingTransactionDetailSummaryData summaryDetail={summaryDetail} />
            </div>
          )}
          {(chartData?.length ?? 0) > 0 && !isDownload && (
            <div className="report-chart">
              <AgCharts options={options} />
            </div>
          )}
        </div>

        {isDownload && isLoading ? (
          <LoadingComponent />
        ) : (
          !isDownload && (
            <BaseGrid
              colDefs={colDefs}
              defaultColDef={defaultColDef}
              getData={getReportData}
              isGridReady={gridReady}
              reportInputs={reportInputs}
              dataSource={dataSource}
              gridApi={gridApi}
              setGridApi={setGridApi}
              getContextMenuItems={getContextMenuItems}
              autoSizeStrategy={autoSizeStrategy}
            />
          )
        )}
      </div>
    </>
  );
};
CardDefundingTransactionDetail.displayName = "CardDefundingTransactionDetail";

export default CardDefundingTransactionDetail;